import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { getWhatsappUrl } from "@/lib/whatsapp";
import { useAuth } from "@/hooks/useAuth";
import useMember from "@/hooks/useMember";
import Cropper, { Area } from "react-easy-crop";
import { logActivity } from "@/lib/activityLog";
import TestimonialForm from "./components/TestimonialForm";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface MemberProfile {
  id: string;
  full_name: string;
  city_state: string;
  profession: string;
  description: string;
  whatsapp: string;
  email: string;
  country_code: string;
  instagram: string;
  website: string;
  photo: string;
  photo_position: string;
  bio_long: string;
  skills: string;
  languages: string;
  education: string;
  services: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  experience_years: string;
  status: string;
  has_physical_business: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
}

function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context not available"));
        return;
      }
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
      );
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    image.onerror = () => reject(new Error("Failed to load image"));
  });
}

export default function Profile() {
  const { id } = useParams<{ id: string }>();
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareTooltip, setShareTooltip] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [editToken, setEditToken] = useState<string | null>(null);
  const { isAuthenticated, logout } = useAuth();
  const { member: approvedMember, loading: memberLoading } = useMember();
  const isApprovedMember = !memberLoading && approvedMember !== null;

  // Troca de foto
  const [showChangePhoto, setShowChangePhoto] = useState(false);
  const [newPhotoPreview, setNewPhotoPreview] = useState<string | null>(null);
  const [photoChanging, setPhotoChanging] = useState(false);
  const [photoChangeError, setPhotoChangeError] = useState("");
  const [photoChangeSuccess, setPhotoChangeSuccess] = useState(false);
  const newPhotoFileRef = useRef<{ file: File; preview: string } | null>(null);

  // Crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Senha de acesso (opcional)
  const [passwordValue, setPasswordValue] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // "Esqueci meu link"
  const [showForgotLink, setShowForgotLink] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState("");

  useEffect(() => {
    async function load() {
      const { data: { session } } = await supabase.auth.getSession();
      const fonte = session ? "members_directory" : "members_public";
      let { data, error } = await supabase
        .from(fonte)
        .select("*")
        .eq("id", id)
        .in("status", ["approved", "active"])
        .maybeSingle();

      if (fonte === "members_directory" && (error || !data)) {
        ({ data, error } = await supabase
          .from("members_public")
          .select("*")
          .eq("id", id)
          .in("status", ["approved", "active"])
          .maybeSingle());
      }

      if (error || !data) {
        setNotFound(true);
      } else {
        setMember(data as MemberProfile);

        logActivity("profile_view", {
          profile_id: id,
          profile_name: data.full_name,
          profile_profession: data.profession || "",
        });

        // Verifica se quem ta vendo e a dona do perfil: compara o id do perfil
        // com o id da integrante autenticada (a view publica nao tem email).
        const { data: myMember } = await supabase.rpc("my_member_profile");
        if (myMember && String(myMember.id) === String(data.id)) {
          setIsOwner(true);
          const { data: token } = await supabase.rpc("my_edit_token");
          setEditToken(token || null);
        }
      }
      setLoading(false);
    }
    if (id) load();
  }, [id]);

  const handleShare = async () => {
    const url = window.location.href;
    const text = `Conheça ${member?.full_name} na comunidade Entre Elas!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url });
      } catch {
        // user cancelled
      }
    } else {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      setShareTooltip(true);
      setTimeout(() => setShareTooltip(false), 2500);
    }
  };

  const handleResendLink = async () => {
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    setForgotError("");

    try {
      const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
      const pathPrefix = basePath ? `/${basePath}` : '';
      const baseUrl = `${window.location.origin}${pathPrefix}`;
      const { data, error: fnError } = await supabase.functions.invoke("resend-edit-link", {
        body: { email: forgotEmail.trim(), baseUrl },
      });

      if (fnError || !data?.success) {
        setForgotError(data?.error || "Erro ao enviar. Tente novamente.");
      } else {
        setForgotSuccess(true);
      }
    } catch {
      setForgotError("Erro ao enviar. Tente novamente.");
    }
    setForgotSending(false);
  };

  const handleNewPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setPhotoChangeError("A foto é muito grande (máx. 5MB). Escolha uma foto menor.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      setNewPhotoPreview(dataUrl);
      newPhotoFileRef.current = { file, preview: dataUrl };
      setPhotoChangeError("");
      setPhotoChangeSuccess(false);
      // Reset crop ao trocar de imagem
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoSubmit = async () => {
    const stored = newPhotoFileRef.current;
    const previewSrc = stored?.preview || newPhotoPreview;
    if (!previewSrc || !member) return;

    setPhotoChanging(true);
    setPhotoChangeError("");
    setPhotoChangeSuccess(false);

    let finalImageDataUrl: string;

    // Se tem crop definido, recorta antes de enviar
    if (croppedAreaPixels && (croppedAreaPixels.width > 0 || croppedAreaPixels.height > 0)) {
      try {
        finalImageDataUrl = await getCroppedImg(previewSrc, croppedAreaPixels);
      } catch {
        setPhotoChangeError("Erro ao processar o recorte da imagem. Tente novamente.");
        setPhotoChanging(false);
        return;
      }
    } else {
      finalImageDataUrl = previewSrc;
    }

    const match = finalImageDataUrl.match(/^data:(.+);base64,(.+)$/);
    if (!match) {
      setPhotoChangeError("Formato da imagem inválido.");
      setPhotoChanging(false);
      return;
    }

    const photoMime = match[1];
    const photoBase64 = match[2];

    try {
      const { data, error: fnError } = await supabase.functions.invoke("update-member-photo", {
        body: {
          memberId: Number(member.id),
          approvalToken: editToken,
          filename: stored?.file?.name || "photo.jpg",
          mimeType: photoMime,
          base64Data: photoBase64,
        },
      });

      if (fnError || !data?.success) {
        setPhotoChangeError(data?.error || fnError?.message || "Erro ao trocar foto. Tente novamente.");
        setPhotoChanging(false);
        return;
      }

      setMember({ ...member, photo: data.url });
      setPhotoChangeSuccess(true);
      setNewPhotoPreview(null);
      newPhotoFileRef.current = null;
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setTimeout(() => setShowChangePhoto(false), 2000);
    } catch (err) {
      setPhotoChangeError((err as Error)?.message || "Erro inesperado. Tente novamente.");
    }
    setPhotoChanging(false);
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordValue.trim() || passwordValue.length < 6) {
      setPasswordError("A senha deve ter no mínimo 6 caracteres.");
      return;
    }

    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess(false);

    const { error: updateError } = await supabase.auth.updateUser({ password: passwordValue });

    if (updateError) {
      setPasswordError(updateError.message || "Erro ao salvar senha. Tente novamente.");
    } else {
      setPasswordSuccess(true);
      setPasswordValue("");
    }
    setPasswordLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown">Carregando perfil...</p>
        </div>
      </div>
    );
  }

  if (notFound || !member) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-nude mx-auto mb-6">
            <i className="ri-user-search-line text-3xl text-brown/30" />
          </div>
          <h2 className="font-display text-2xl text-brown mb-3">Perfil não encontrado</h2>
          <p className="font-body text-brown/70 mb-6">Este perfil pode não estar disponível ou ainda não foi aprovado.</p>
          <Link
            to="/comunidade"
            className="inline-block font-body text-sm px-8 py-3 rounded-full bg-primary text-white tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            Ver comunidade
          </Link>
        </div>
      </div>
    );
  }

  const skills = member.skills ? member.skills.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const hasSocialLinks = member.instagram || member.linkedin || member.facebook || member.tiktok || member.website;
  const whatsappUrl = member.whatsapp ? getWhatsappUrl(member.whatsapp, member.country_code) : null;

  const businessAddressParts = [
    member.business_name,
    member.business_address,
    member.business_city,
    member.business_state,
    member.business_zip,
  ].filter(Boolean).join(", ");
  const hasMapsLocation = !!(member.business_city?.trim() && member.business_state?.trim());
  const mapsUrl = hasMapsLocation
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddressParts)}`
    : null;

  return (
    <div className="min-h-screen bg-beige">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="font-display text-xl md:text-2xl tracking-wide whitespace-nowrap text-brown">
              Entre Elas
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/comunidade" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                Comunidade
              </Link>
              <Link to="/meu-perfil" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                Meu Perfil
              </Link>
              {isAuthenticated && (
                <button
                  onClick={logout}
                  className="text-sm font-body text-brown/50 tracking-wide uppercase transition-colors duration-300 hover:text-red-500 whitespace-nowrap cursor-pointer"
                >
                  Sair
                </button>
              )}
              <Link to="/cadastro" className="text-sm font-body text-brown tracking-wide uppercase transition-colors duration-300 hover:text-primary whitespace-nowrap">
                Cadastro
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 md:pt-28 pb-8 md:pb-12">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <div className="flex flex-col md:flex-row">
              {/* Photo side */}
              <div className="md:w-2/5 lg:w-1/3 relative">
                <div className="aspect-[3/4] md:aspect-[3/4] w-full relative">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.full_name}
                      className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(member.photo_position)]}`}
                    />
                  ) : (
                    <MemberPhotoPlaceholder size="xl" rounded="none" name={member.full_name} />
                  )}
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-primary via-primary-dark to-brown" />
                </div>
              </div>

              {/* Info side */}
              <div className="md:w-3/5 lg:w-2/3 p-6 md:p-10 lg:p-12 flex flex-col justify-center bg-nude/30">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="font-display text-2xl md:text-3xl lg:text-4xl text-brown mb-1">
                      {member.full_name}
                    </h1>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-block w-10 h-px bg-primary/50" />
                      <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                      <span className="inline-block w-10 h-px bg-primary/50" />
                      <p className="font-body text-base md:text-lg text-primary font-medium tracking-wide uppercase">
                        {member.profession}
                      </p>
                    </div>
                    {member.city_state && (
                      <p className="font-body text-sm text-brown/60 flex items-center gap-1">
                        <i className="ri-map-pin-line text-primary" />
                        {member.city_state}
                      </p>
                    )}
                  </div>

                  <div className="relative">
                    <button
                      onClick={handleShare}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white hover:bg-primary-dark transition-all duration-300 cursor-pointer"
                      title="Compartilhar perfil"
                    >
                      <i className="ri-share-line text-lg" />
                    </button>
                    {shareTooltip && (
                      <div className="absolute right-0 top-12 bg-brown text-white text-xs font-body px-3 py-2 rounded-lg whitespace-nowrap animate-in fade-in slide-in-from-top-2">
                        Link copiado!
                      </div>
                    )}
                  </div>
                </div>

                {/* Meus anúncios — only for owner */}
                {isOwner && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    <Link
                      to="/meus-anuncios"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-body tracking-wide hover:bg-primary-dark transition-colors whitespace-nowrap"
                    >
                      <i className="ri-megaphone-line" />
                      Meus anúncios
                    </Link>
                  </div>
                )}

                {/* Edit button — only for owner */}
                {isOwner && editToken && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    <Link
                      to={`/completar-cadastro?token=${editToken}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-body tracking-wide hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                    >
                      <i className="ri-edit-line" />
                      Editar meu perfil
                    </Link>
                    <button
                      onClick={() => setShowChangePhoto(!showChangePhoto)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-body tracking-wide hover:bg-primary hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-camera-line" />
                      Trocar foto
                    </button>
                  </div>
                )}

                {/* Modal / Painel de troca de foto com crop */}
                {showChangePhoto && isOwner && editToken && (
                  <div className="mb-6 bg-white rounded-xl border border-nude p-5 relative">
                    <button
                      onClick={() => { setShowChangePhoto(false); setNewPhotoPreview(null); setPhotoChangeError(""); setPhotoChangeSuccess(false); newPhotoFileRef.current = null; setCrop({ x: 0, y: 0 }); setZoom(1); setCroppedAreaPixels(null); }}
                      className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-lg" />
                    </button>
                    <h4 className="font-display text-lg text-brown mb-4">
                      {newPhotoPreview ? "Ajuste o enquadramento" : "Trocar foto do perfil"}
                    </h4>

                    {!newPhotoPreview ? (
                      /* Etapa 1: selecionar a imagem */
                      <div className="flex flex-col sm:flex-row gap-5 items-start">
                        <div className="shrink-0 text-center">
                          <p className="font-body text-xs text-brown/50 mb-2">Foto atual</p>
                          {member.photo ? (
                            <img
                              src={member.photo}
                              alt="Foto atual"
                              className={`w-24 h-24 rounded-xl object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(member.photo_position)]}`}
                            />
                          ) : (
                            <MemberPhotoPlaceholder size="lg" rounded="xl" name={member.full_name} />
                          )}
                        </div>
                        <div className="flex-1">
                          <label className="block w-full">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleNewPhotoChange}
                              className="hidden"
                            />
                            <div className="w-full h-32 rounded-xl border-2 border-dashed border-nude hover:border-primary bg-beige/40 flex flex-col items-center justify-center cursor-pointer transition-colors">
                              <div className="flex flex-col items-center text-brown/40">
                                <i className="ri-camera-line text-2xl mb-1" />
                                <span className="text-sm font-body">Clique para escolher nova foto</span>
                                <span className="text-xs font-body mt-1 text-brown/30">JPG, PNG (máx. 5MB)</span>
                              </div>
                            </div>
                          </label>
                          {photoChangeError && (
                            <p className="font-body text-xs text-red-500 mt-2">{photoChangeError}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* Etapa 2: crop / zoom da imagem */
                      <div>
                        {/* Área de crop */}
                        <div className="relative w-full bg-beige/50 rounded-xl overflow-hidden" style={{ height: 320 }}>
                          <Cropper
                            image={newPhotoPreview}
                            crop={crop}
                            zoom={zoom}
                            aspect={3 / 4}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onCropComplete={onCropComplete}
                            onZoomChange={setZoom}
                          />
                        </div>

                        {/* Slider de zoom */}
                        <div className="flex items-center gap-3 mt-4 px-1">
                          <i className="ri-zoom-out-line text-brown/50 text-sm" />
                          <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 h-1.5 rounded-full bg-nude appearance-none cursor-pointer accent-primary"
                          />
                          <i className="ri-zoom-in-line text-brown/50 text-sm" />
                        </div>

                        {photoChangeError && (
                          <p className="font-body text-xs text-red-500 mt-3">{photoChangeError}</p>
                        )}
                        {photoChangeSuccess && (
                          <p className="font-body text-xs text-green-600 mt-3 flex items-center gap-1">
                            <i className="ri-check-line" />
                            Foto atualizada com sucesso!
                          </p>
                        )}

                        {/* Botões de ação */}
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={handlePhotoSubmit}
                            disabled={photoChanging}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-body tracking-wide uppercase hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            {photoChanging ? (
                              <>
                                <i className="ri-loader-4-line animate-spin" />
                                Salvando...
                              </>
                            ) : (
                              <>
                                <i className="ri-check-line" />
                                Salvar nova foto
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => { setNewPhotoPreview(null); newPhotoFileRef.current = null; setPhotoChangeError(""); setCrop({ x: 0, y: 0 }); setZoom(1); setCroppedAreaPixels(null); }}
                            disabled={photoChanging}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-nude text-brown text-sm font-body hover:bg-beige transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-arrow-go-back-line" />
                            Escolher outra
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quick stats */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {member.experience_years && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 font-body text-sm text-brown">
                      <i className="ri-briefcase-line text-primary" />
                      {member.experience_years} de experiência
                    </div>
                  )}
                  {member.languages && (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 font-body text-sm text-brown">
                      <i className="ri-global-line text-primary" />
                      {member.languages}
                    </div>
                  )}
                </div>

                <p className="font-body text-brown/70 leading-relaxed text-sm md:text-base mb-6">
                  {member.description}
                </p>

                {/* Contact buttons */}
                {(whatsappUrl || member.email) && (
                  <div className="flex flex-wrap gap-3">
                    {whatsappUrl && (
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-white text-sm font-body tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line" /> WhatsApp
                      </a>
                    )}
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-primary text-primary text-sm font-body tracking-wide uppercase hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                      >
                        <i className="ri-mail-line" /> Email
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Senha de acesso — apenas para a dona do perfil */}
      {isOwner && (
        <section className="pb-8 md:pb-12">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
              <div className="flex items-start gap-4 mb-5">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 shrink-0 mt-0.5">
                  <i className="ri-lock-line text-primary text-lg" />
                </span>
                <div>
                  <h3 className="font-display text-lg text-brown mb-1">
                    Criar ou alterar senha de acesso
                  </h3>
                  <p className="font-body text-sm text-brown/50 leading-relaxed">
                    Isso é totalmente <strong>opcional</strong>. Você pode continuar usando o Magic Link normalmente
                    mesmo sem definir uma senha. Se preferir, cadastre uma senha aqui para também poder
                    entrar com email e senha na tela de login.
                  </p>
                </div>
              </div>

              {passwordSuccess ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-green-50 text-green-700 font-body text-sm">
                  <i className="ri-check-line text-lg" />
                  Senha salva com sucesso! Agora você também pode entrar com email e senha na tela de acesso.
                </div>
              ) : (
                <form onSubmit={handlePasswordSave} className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 relative">
                    <input
                      type={showPasswordField ? "text" : "password"}
                      value={passwordValue}
                      onChange={(e) => { setPasswordValue(e.target.value); setPasswordError(""); }}
                      placeholder="Nova senha (mínimo 6 caracteres)"
                      minLength={6}
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordField(!showPasswordField)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-brown/40 hover:text-brown/70 transition-colors cursor-pointer"
                      tabIndex={-1}
                    >
                      <i className={showPasswordField ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {passwordLoading ? (
                      <span className="flex items-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />
                        Salvando...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <i className="ri-check-line" />
                        Salvar senha
                      </span>
                    )}
                  </button>
                </form>
              )}

              {passwordError && (
                <p className="font-body text-sm text-red-500 mt-3">{passwordError}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Depoimento sobre a comunidade — apenas para a dona do perfil */}
      {isOwner && <TestimonialForm memberId={member.id} />}

      {/* Bio Section */}
      {member.bio_long && (
        <section className="pb-8 md:pb-12">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 md:p-10 lg:p-12 relative overflow-hidden shadow-sm">
              <div className="absolute -top-4 -right-4 w-32 h-32 border-2 border-primary/10 rounded-full" />
              <div className="absolute -top-2 -right-2 w-24 h-24 border border-primary/8 rounded-full" />
              <div className="absolute top-8 bottom-8 left-0 w-1 bg-gradient-to-b from-transparent via-primary/30 to-transparent rounded-full" />
              <h3 className="font-display text-xl md:text-2xl text-brown mb-4 flex items-center gap-2 relative z-10 pl-2">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
                  <i className="ri-quill-pen-line text-primary text-lg" />
                </span>
                Minha História
              </h3>
              <p className="font-body text-brown/70 leading-relaxed text-sm md:text-base whitespace-pre-line relative z-10 pl-2">
                {member.bio_long}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Skills + Education + Services Grid */}
      <section className="pb-8 md:pb-12">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {skills.length > 0 && (
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary/30 via-primary to-primary/30" />
                <h3 className="font-display text-lg text-brown mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary/10">
                    <i className="ri-star-line text-primary" />
                  </span>
                  Habilidades
                </h3>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, i) => {
                    const bgColors = ["bg-nude/70", "bg-primary/8", "bg-beige"];
                    return (
                      <span
                        key={skill}
                        className={`px-4 py-2 rounded-full font-body text-sm text-brown transition-all duration-300 cursor-default ${bgColors[i % 3]}`}
                      >
                        {skill}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {member.education && (
              <div className="bg-nude/40 rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-brown/20 via-brown/50 to-brown/20" />
                <h3 className="font-display text-lg text-brown mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-brown/10">
                    <i className="ri-graduation-cap-line text-brown" />
                  </span>
                  Formação
                </h3>
                <p className="font-body text-brown/70 leading-relaxed text-sm whitespace-pre-line">
                  {member.education}
                </p>
              </div>
            )}

            {member.services && (
              <div className="bg-white rounded-2xl p-6 md:p-8 md:col-span-2 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary/30 via-primary to-primary/30" />
                <h3 className="font-display text-lg text-brown mb-4 flex items-center gap-2">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary/10">
                    <i className="ri-service-line text-primary" />
                  </span>
                  Serviços
                </h3>
                <p className="font-body text-brown/70 leading-relaxed text-sm whitespace-pre-line">
                  {member.services}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Onde me encontrar */}
      {member.has_physical_business && (
        <section className="pb-8 md:pb-12">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary/30 via-primary to-primary/30" />
              <h3 className="font-display text-lg text-brown mb-4 flex items-center gap-2">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary/10">
                  <i className="ri-store-2-line text-primary" />
                </span>
                Onde me encontrar
              </h3>
              <div className="pl-2">
                {member.business_name && (
                  <p className="font-display text-base md:text-lg text-brown mb-1">
                    {member.business_name}
                  </p>
                )}
                <p className="font-body text-brown/70 leading-relaxed text-sm md:text-base">
                  {[
                    member.business_address,
                    member.business_city,
                    member.business_state,
                    member.business_zip,
                  ].filter(Boolean).join(", ") || "Endereço não informado"}
                </p>
                {mapsUrl && (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-body tracking-wide hover:bg-primary-dark transition-colors whitespace-nowrap"
                  >
                    <i className="ri-map-pin-line" />
                    Ver no mapa
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Social Links — only for approved members or the profile owner */}
      {(isApprovedMember || isOwner) && hasSocialLinks && (
        <section className="pb-8 md:pb-12">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-nude/80 via-nude/40 to-beige rounded-2xl p-6 md:p-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 border-l-2 border-b-2 border-primary/10 rounded-bl-3xl" />
              <h3 className="font-display text-lg text-brown mb-6 text-center">
                Me encontre nas redes
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-4">
                {member.instagram && (
                  <a
                    href={`https://instagram.com/${member.instagram.replace("@", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 font-body text-sm shadow-sm whitespace-nowrap"
                  >
                    <i className="ri-instagram-line" /> Instagram
                  </a>
                )}
                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 font-body text-sm shadow-sm whitespace-nowrap"
                  >
                    <i className="ri-linkedin-line" /> LinkedIn
                  </a>
                )}
                {member.facebook && (
                  <a
                    href={member.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 font-body text-sm shadow-sm whitespace-nowrap"
                  >
                    <i className="ri-facebook-line" /> Facebook
                  </a>
                )}
                {member.tiktok && (
                  <a
                    href={`https://tiktok.com/${member.tiktok.replace("@", "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 font-body text-sm shadow-sm whitespace-nowrap"
                  >
                    <i className="ri-tiktok-line" /> TikTok
                  </a>
                )}
                {member.website && (
                  <a
                    href={member.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 rounded-full bg-white text-brown hover:bg-primary hover:text-white transition-all duration-300 font-body text-sm shadow-sm whitespace-nowrap"
                  >
                    <i className="ri-global-line" /> Site
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Convite para ver redes sociais — aparece quando tem redes mas o visitante não tem acesso */}
      {!isApprovedMember && !isOwner && hasSocialLinks && (
        <section className="pb-8 md:pb-12">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-nude/80 via-nude/40 to-beige rounded-2xl p-6 md:p-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 mx-auto mb-4">
                <i className="ri-lock-line text-primary text-xl" />
              </div>
              <h3 className="font-display text-lg text-brown mb-2">
                Redes sociais e contatos
              </h3>
              <p className="font-body text-sm text-brown/60 leading-relaxed max-w-md mx-auto mb-4">
                Os contatos e redes sociais são visíveis apenas para integrantes da comunidade. Faça login ou cadastre-se para acessar.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  to="/meu-perfil"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  <i className="ri-login-box-line" />
                  Fazer login
                </Link>
                <Link
                  to="/cadastro"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary text-primary font-body text-sm hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                >
                  <i className="ri-user-add-line" />
                  Cadastre-se
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Access profile section — hidden for the owner (she already has edit buttons) */}
      {!isOwner && (
      <section className="pb-8 md:pb-12">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl p-6 md:p-8 text-center shadow-sm">
            {!showForgotLink ? (
              <button
                onClick={() => setShowForgotLink(true)}
                className="font-body text-sm text-brown/50 hover:text-primary transition-colors underline cursor-pointer"
              >
                É dona deste perfil? Acesse sua página de edição
              </button>
            ) : (
              <div className="max-w-md mx-auto">
                <p className="font-body text-sm text-brown/70 mb-6">
                  Agora você pode acessar seu perfil de forma rápida e segura:
                </p>

                {/* Primary: Magic Link */}
                <Link
                  to="/meu-perfil"
                  className="flex items-center gap-3 w-full px-5 py-4 rounded-xl bg-primary/8 hover:bg-primary/15 border border-primary/15 transition-colors text-left mb-4 group"
                >
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary text-white shrink-0 group-hover:scale-105 transition-transform">
                    <i className="ri-magic-line text-lg" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm font-medium text-brown group-hover:text-primary transition-colors">
                      Fazer login com Magic Link
                    </p>
                    <p className="font-body text-xs text-brown/50">
                      Digite seu email e receba um link mágico de acesso
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-brown/30 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                </Link>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="flex-1 h-px bg-nude" />
                  <span className="font-body text-xs text-brown/40 uppercase tracking-wider">ou</span>
                  <span className="flex-1 h-px bg-nude" />
                </div>

                {/* Fallback: Email edit link */}
                {forgotSuccess ? (
                  <div className="py-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-4">
                      <i className="ri-mail-send-line text-xl text-green-500" />
                    </div>
                    <p className="font-body text-brown mb-2">Email enviado!</p>
                    <p className="font-body text-sm text-brown/60">
                      Verifique sua caixa de entrada. Se não encontrar, olhe a pasta de spam.
                    </p>
                    <button
                      onClick={() => { setShowForgotLink(false); setForgotSuccess(false); setForgotEmail(""); }}
                      className="mt-4 font-body text-sm text-brown/50 hover:text-primary transition-colors underline cursor-pointer"
                    >
                      Fechar
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="font-body text-xs text-brown/50 mb-3">
                      Prefere o método antigo? Enviaremos seu link de edição por email:
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="seu@email.com"
                        className="flex-1 px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      />
                      <button
                        onClick={handleResendLink}
                        disabled={forgotSending || !forgotEmail.trim()}
                        className="px-5 py-3 rounded-lg bg-primary text-white font-body text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {forgotSending ? (
                          <i className="ri-loader-4-line animate-spin" />
                        ) : (
                          "Enviar"
                        )}
                      </button>
                    </div>
                    {forgotError && (
                      <p className="font-body text-sm text-red-500 mt-3">{forgotError}</p>
                    )}
                  </div>
                )}

                <button
                  onClick={() => { setShowForgotLink(false); setForgotError(""); setForgotSuccess(false); }}
                  className="mt-4 font-body text-xs text-brown/40 hover:text-brown/60 transition-colors underline cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* CTA - Join the community — hidden for the profile owner */}
      {!isOwner && (
      <section className="pb-16 md:pb-24">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-primary via-primary-dark to-brown p-8 md:p-14 text-center">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-16 border-2 border-b-0 border-white/10 rounded-t-full" />
            <div className="relative z-10">
              <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
                Você também pode fazer parte!
              </h2>
              <p className="font-body text-white/80 text-base leading-relaxed max-w-xl mx-auto mb-8">
                Assim como <strong>{member.full_name.split(" ")[0]}</strong>, você pode divulgar seu trabalho,
                conectar-se com outras mulheres empreendedoras e expandir sua rede.
                Cadastre-se gratuitamente e ganhe sua página de apresentação.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/cadastro"
                  className="inline-block font-body text-sm px-10 py-4 rounded-full bg-white text-primary tracking-wide uppercase hover:bg-white/90 transition-colors whitespace-nowrap"
                >
                  Quero meu perfil também
                </Link>
                <Link
                  to="/comunidade"
                  className="inline-block font-body text-sm px-8 py-4 rounded-full border border-white/40 text-white tracking-wide uppercase hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  Ver comunidade
                </Link>
              </div>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4" />
          </div>
        </div>
      </section>
      )}

      {/* Footer */}
      <footer className="bg-brown py-8">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-5xl mx-auto text-center">
          <p className="font-body text-sm text-white/60">
            &copy; {new Date().getFullYear()} Entre Elas. Todos os direitos reservados.
          </p>
          <p className="font-body text-xs text-white/40 mt-1">
            Uma comunidade de mulheres que se apoiam e crescem juntas.
          </p>
        </div>
      </footer>
    </div>
  );
}